import { useEffect, useId } from "react";
import { confirmAction } from "./feedback.tsx";

/**
 * Unsaved edits, tracked across the app so leaving can ask first.
 *
 * Each editor reports whether it holds edits that are not saved; while any
 * does, closing the tab or refreshing warns (the browser's own prompt),
 * following an in-app link asks (see `guardLinks`), and closing a side panel
 * asks (see `Drawer`). The browser's Back button is not covered: the app's
 * router cannot hold a history step, and faking it would break Back for
 * everything else.
 */

const dirty = new Set<string>();

function hasUnsavedChanges(): boolean {
  return dirty.size > 0;
}

/** Ask whether to drop unsaved edits; true when there are none, or the user agrees. */
export async function confirmDiscard(): Promise<boolean> {
  if (!hasUnsavedChanges()) return true;
  const ok = await confirmAction({
    title: "Leave without saving?",
    message: "You have changes that haven't been saved. They'll be lost.",
    confirmLabel: "Discard changes",
    destructive: true,
  });
  if (ok) dirty.clear();
  return ok;
}

export function useUnsavedChanges(isDirty: boolean): void {
  const id = useId();
  useEffect(() => {
    if (isDirty) dirty.add(id);
    else dirty.delete(id);
    return () => {
      dirty.delete(id);
    };
  }, [id, isDirty]);
}

/**
 * Installed once by the app shell. Catches clicks on in-app links while
 * anything is unsaved, asks, and only then navigates. Runs in the capture
 * phase on the document, ahead of the router's own click handling.
 */
export function guardLinks(navigate: (to: string) => void): () => void {
  const onClick = (e: MouseEvent) => {
    if (!hasUnsavedChanges() || e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    e.stopPropagation();
    void confirmDiscard().then((ok) => {
      if (ok) navigate(url.pathname + url.search + url.hash);
    });
  };
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!hasUnsavedChanges()) return;
    e.preventDefault();
    e.returnValue = "";
  };
  document.addEventListener("click", onClick, true);
  window.addEventListener("beforeunload", onBeforeUnload);
  return () => {
    document.removeEventListener("click", onClick, true);
    window.removeEventListener("beforeunload", onBeforeUnload);
  };
}
